import React, { FC, ReactNode } from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';

import Loading from '@components/loading';
import NoConnector from '@components/settings/components/common/noConnector';
import ConnectorOff from '@components/settings/components/common/connectorOff';
import Refresh from '@components/common/Refresh';
import TouchableElement from '@components/common/touchableElement';
import AddMachine from '@components/settings/v2/structure/machines/machines/addMachine';
import Svg from '@styles/icons/svg';
import { font } from '@styles/fonts';
import { scale, fullHeight, height } from '@components/frame';
import { isPhone, Popup } from '@helpers';
import { ColorFunction } from '@styles/colors/types';

import { useMachineScreenContentState } from '@v2/pages/machines/hooks/useMachineScreenContentState';
import { MachineScreenContentState } from '@v2/pages/machines/common/enums';
import { NotSelectedProps } from '@v2/pages/machines/common/types/props';
import { usePanelViewMode } from '@v2/pages/machines/hooks/usePanelViewMode';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';

const NotSelected: FC<NotSelectedProps> = ({ isMain = false, message, C, detailsWidth }) => {
  return (
    <View
      style={[
        {
          flex: 1,
          height: '100%',
          width: '100%',
          alignContent: 'center',
          alignItems: 'center',
          justifyContent: 'center',
        },
        isMain
          ? { width: detailsWidth }
          : { width: '100%', minHeight: scale(100) },
      ]}
    >
      <Text
        style={[
          {
            color: C('Label / Light / Tertiary'),
            fontWeight: 'normal',
            marginTop: scale(-9),
            fontSize: 28,
            letterSpacing: scale(0.6),
            textAlign: 'center',
          },
          font.Body,
        ]}
      >
        {i18n.t(message || 'No Machine Selected')}
      </Text>
    </View>
  );
};

interface MachinesScreenContentRendererProps {
  C: ColorFunction;
  children: ReactNode;
}

const MachinesScreenContentRenderer: FC<MachinesScreenContentRendererProps> = ({ C, children }) => {
  const { contentState, connectorTime, message, refetch } = useMachineScreenContentState();
  const { panelViewMode } = usePanelViewMode();
  const { detailsWidth } = useDeviceSize();

  const s = scale;

  switch (contentState) {
    case MachineScreenContentState.LOADING:
      return <Loading />;

    case MachineScreenContentState.NO_CONNECTOR:
      return (
        <View style={{ backgroundColor: C('Background/Light/System'), flex: 1 }}>
          <NoConnector time={connectorTime} />
        </View>
      );

    case MachineScreenContentState.CONNECTOR_OFF:
      return (
        <View style={{ backgroundColor: C('Background/Light/System'), flex: 1 }}>
          <ConnectorOff time={connectorTime} />
        </View>
      );

    case MachineScreenContentState.ONBOARDING:
      return (
        <View
          style={{
            backgroundColor: C('Background/Light/System'),
            flex: 1,
            justifyContent: 'center',
            alignContent: 'center',
            alignItems: 'center',
          }}
        >
          <Text style={[font['Large Title'], { color: C('Label/Light/Primary'), textAlign: 'center', marginBottom: scale(28) }]}>
            {i18n.t('Connecting...')}
          </Text>
          <Svg icon="connecting" width={scale(88)} height={scale(88)} color="Gray/Light/2" />
        </View>
      );

    case MachineScreenContentState.ERROR:
      return <Refresh onPress={refetch} />;

    case MachineScreenContentState.NO_MACHINES:
      return (
        <View
          style={{
            width: '100%',
            height: panelViewMode ? fullHeight : height,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: C('Background/Light/System'),
          }}
        >
          <TouchableElement
            onPress={() =>
              Popup({
                Component: AddMachine,
                width: 580,
              })
            }
          >
            {({ boxStyle, pressHoverColor }) => (
              <View
                style={[
                  boxStyle,
                  {
                    width: s(324),
                    height: s(112),
                    borderRadius: s(12),
                    backgroundColor: C(pressHoverColor('Button/Light/Primary')),
                  },
                ]}
              >
                <View style={{ width: '100%', alignItems: 'center', marginTop: s(23) }}>
                  <View
                    style={{
                      backgroundColor: C('Label/Light/AlwaysLight Primary'),
                      width: s(27),
                      height: s(27),
                      borderRadius: s(27) / 2,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Svg icon="plus" width={s(18)} height={s(18)} color={'Button/Light/Primary'} />
                  </View>
                </View>
                <View style={{ width: '100%', alignItems: 'center', marginTop: s(14) }}>
                  <Text style={[font['Title 2'], { color: C('Button/Light/Primary Text') }]}>{i18n.t('Add Machine')}</Text>
                </View>
              </View>
            )}
          </TouchableElement>
        </View>
      );

    case MachineScreenContentState.NO_MACHINE_SELECTED:
    case MachineScreenContentState.STATISTICS_ONLY:
    case MachineScreenContentState.NO_MACHINE_IN_PERIOD:
    case MachineScreenContentState.NO_DATA_IN_PERIOD:
      return <NotSelected isMain={!isPhone} message={message} C={C} detailsWidth={detailsWidth} />;

    case MachineScreenContentState.READY:
      return <>{children}</>;

    default:
      return <>{children}</>;
  }
};

export default MachinesScreenContentRenderer;
